import type { Alignment, Side } from 'driver.js';
import { findTourTarget, resolveTourSteps, runTour, type TourHandle, type TourStepDefinition } from './tour-engine';

/**
 * Yonlendirme Faz 4.2 — sayfa tanimlari. Hem sayfa turlari (sayfa ilk kez
 * acildiginda) hem de karsilama turu (sayfalari sirayla gezen yolculuk) ayni
 * tanimlari ve metinleri kullanir.
 *
 * Her sayfanin ilk adimi sayfanin tanitimidir (ortak `PageHeader` karti ya da
 * sayfanin ana alani): "bu sayfa nedir, ne zaman kullanilir". Sonraki adimlar
 * sayfanin onemli parcalaridir. Metinler `pageTour.<pageId>.intro.title|desc`
 * ve `pageTour.<pageId>.<n>.title|desc` anahtarlarindadir (n 1'den baslar).
 * Hedefi sayfada olmayan adim (orn. bekleyen istek yoksa Onayla/Reddet) atlanir.
 *
 * "Goruldu" bilgisi yalnizca bu cihazda tutulur
 * (`wc.pageTour.seen.<pageId>.<userId>`): baska cihazda sayfa turu bir kez
 * daha gorulebilir; karsilama turundan farkli olarak sunucuya yazilmaz.
 */

export const PAGE_TOUR_START_EVENT = 'wc:page-tour:start';
/** Ortak sayfa basligi kartinin (`PageHeader`) tur hedefi. */
export const PAGE_HEADER_TOUR_ID = 'page-header';

type PageTourStepInput = { target: string; side: Side; align?: Alignment };

export type PageTourDefinition = {
    pageId: string;
    /** Sayfa yolu (bastaki/sondaki `/` olmadan, sorgu haric). */
    path: string;
    /** Sayfanin menudeki adi (ceviri anahtari); karsilama turunda sayacin onunde gorunur. */
    labelKey: string;
    /** Sayfa verisi cizildiginde gorunen hedef; sayfa turu gorunene kadar bekler. */
    readyTarget: string;
    /** Ilk adim sayfanin tanitimi, sonrakiler ayrintilar. */
    steps: TourStepDefinition[];
};

type PageTourInput = {
    pageId: string;
    path: string;
    labelKey: string;
    readyTarget: string;
    /** Tanitim adiminin hedefi; varsayilan ortak sayfa basligi. */
    intro?: PageTourStepInput;
    details: PageTourStepInput[];
};

const pageStep = (pageId: string, key: string, { target, side, align }: PageTourStepInput): TourStepDefinition => ({
    titleKey: `pageTour.${pageId}.${key}.title`,
    descKey: `pageTour.${pageId}.${key}.desc`,
    targets: [target],
    side,
    align,
});

const definePageTour = ({
    pageId,
    path,
    labelKey,
    readyTarget,
    intro = { target: PAGE_HEADER_TOUR_ID, side: 'bottom', align: 'start' },
    details,
}: PageTourInput): PageTourDefinition => ({
    pageId,
    path,
    labelKey,
    readyTarget,
    steps: [
        pageStep(pageId, 'intro', intro),
        ...details.map((detail, index) => pageStep(pageId, String(index + 1), detail)),
    ],
});

export const PAGE_TOURS: PageTourDefinition[] = [
    definePageTour({
        pageId: 'events',
        path: 'event',
        labelKey: 'menu.event',
        readyTarget: 'event-card',
        details: [
            { target: 'event-card', side: 'right', align: 'start' },
            // Ilk kartin durumuna gore yalnizca biri sayfadadir; digeri atlanir.
            { target: 'event-join', side: 'top', align: 'start' },
            { target: 'event-joined', side: 'top', align: 'start' },
        ],
    }),
    definePageTour({
        pageId: 'companyList',
        path: 'company-list',
        labelKey: 'menu.companies',
        readyTarget: 'company-card-actions',
        details: [
            { target: 'company-list-filters', side: 'bottom', align: 'start' },
            { target: 'company-card-actions', side: 'top', align: 'center' },
        ],
    }),
    definePageTour({
        pageId: 'meetingRequests',
        path: 'meeting-requests',
        labelKey: 'menu.meetingRequests',
        readyTarget: 'meeting-requests-list',
        details: [
            { target: 'meeting-requests-tabs', side: 'bottom', align: 'start' },
            { target: 'meeting-request-decision', side: 'left', align: 'center' },
            { target: 'meeting-requests-create', side: 'bottom', align: 'end' },
        ],
    }),
    definePageTour({
        pageId: 'sessions',
        path: 'sessions',
        labelKey: 'menu.b2bSessionsApply',
        readyTarget: 'session-card',
        details: [
            { target: 'session-card', side: 'right', align: 'start' },
            { target: 'session-steps', side: 'top', align: 'center' },
            { target: 'session-actions', side: 'top', align: 'end' },
        ],
    }),
    definePageTour({
        pageId: 'tables',
        path: 'tables',
        labelKey: 'menu.masalar',
        readyTarget: 'tables-results',
        details: [
            { target: 'tables-sessions', side: 'bottom', align: 'start' },
            { target: 'tables-filters', side: 'bottom', align: 'start' },
            { target: 'tables-book', side: 'top', align: 'center' },
        ],
    }),
    definePageTour({
        pageId: 'myAppointments',
        path: 'my-appointments',
        labelKey: 'menu.myAppointments',
        readyTarget: 'appointments-list',
        details: [
            { target: 'appointments-list', side: 'top', align: 'start' },
            { target: 'appointments-cancel', side: 'left', align: 'center' },
            { target: 'appointments-cancelled', side: 'bottom', align: 'end' },
        ],
    }),
    definePageTour({
        pageId: 'messages',
        path: 'messages',
        labelKey: 'menu.messages',
        readyTarget: 'messages-conversations',
        // Mesajlar sayfasinda baslik karti yok; konusma listesi sayfayi tanitir.
        intro: { target: 'messages-conversations', side: 'right', align: 'start' },
        details: [{ target: 'messages-new', side: 'right', align: 'start' }],
    }),
    definePageTour({
        pageId: 'digitalCards',
        path: 'digital-cards',
        labelKey: 'menu.digitalBusinessCards',
        readyTarget: 'digital-cards-add',
        details: [{ target: 'digital-cards-add', side: 'bottom', align: 'end' }],
    }),
];

const normalizePath = (pathname: string) => pathname.split(/[?#]/)[0].replace(/^\/+|\/+$/g, '');

/** Sayfanin turu; tanimli degilse `null`. */
export const findPageTour = (pathname: string): PageTourDefinition | null => {
    const path = normalizePath(pathname);
    return PAGE_TOURS.find((tour) => tour.path === path) ?? null;
};

export const findPageTourById = (pageId: string): PageTourDefinition | null =>
    PAGE_TOURS.find((tour) => tour.pageId === pageId) ?? null;

/** Sayfa verisi cizildi mi (hazir hedefi gorunuyor mu)? */
export const isPageTourReady = (tour: PageTourDefinition, root: ParentNode = document) =>
    Boolean(findTourTarget([tour.readyTarget], undefined, root));

// ---------- "Goruldu" bilgisi (yalnizca bu cihaz) ----------

export const pageTourSeenStorageKey = (pageId: string, userId: string) => `wc.pageTour.seen.${pageId}.${userId}`;

export const readPageTourSeen = (pageId: string, userId: string) => {
    try {
        return localStorage.getItem(pageTourSeenStorageKey(pageId, userId)) === '1';
    } catch {
        // Depolama kapaliysa tur her seferinde acilmasin.
        return true;
    }
};

export const markPageTourSeen = (pageId: string, userId: string) => {
    if (!userId) return;
    try {
        localStorage.setItem(pageTourSeenStorageKey(pageId, userId), '1');
    } catch {
        // Depolama kapaliysa yapilacak bir sey yok.
    }
};

/** Bulunulan sayfanin turunu (goruldu bilgisinden bagimsiz) baslatir. */
export const requestPageTour = () => window.dispatchEvent(new Event(PAGE_TOUR_START_EVENT));

/** Gorunen adimlarla sayfa turunu baslatir; gosterilecek adim yoksa `null` doner. */
export const startPageTour = ({
    tour,
    t,
    onClose,
}: {
    tour: PageTourDefinition;
    t: (key: string) => string;
    onClose: () => void;
}): TourHandle | null => {
    const steps = resolveTourSteps(tour.steps);
    if (steps.length === 0) return null;
    return runTour({ steps, t, onClose });
};
